import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { authClient } from "@/lib/auth-client";
import { useAuthStore } from "@/store/useAuthStore";
import { isTeacher } from "@/utils/role-utils";

const AUTH_URL =
  import.meta.env.VITE_BETTER_AUTH_URL || "http://localhost:3001";

/**
 * Landing page for a support-login link.
 *
 * The link carries a single-use grant in the URL *fragment*, and this
 * exchanges it for a session. A fragment is never sent to the server, so the
 * secret cannot reach a CDN or reverse-proxy access log — which a `?grant=`
 * query string would, on the very first GET, before any of this code runs.
 * The page still scrubs it from history, so it does not survive in the address
 * bar or a copied URL.
 *
 * Public by necessity: whoever opens the link is not signed in as anyone yet.
 */
const SupportLogin = () => {
  const navigate = useNavigate();
  const syncSession = useAuthStore((state) => state.syncSession);
  const clearAuth = useAuthStore((state) => state.clearAuth);
  const [error, setError] = useState<string | null>(null);
  // True when a redeemed session may still be live: sign-out failed, so
  // there is no safe navigation to offer from this page.
  const [sessionMayBeLive, setSessionMayBeLive] = useState(false);
  const redeemed = useRef(false);

  // Read once, before the scrub below removes it.
  const grant = useRef(
    new URLSearchParams(window.location.hash.replace(/^#/, "")).get("grant"),
  ).current;

  useEffect(() => {
    // The grant is single-use: a double invocation (StrictMode, a re-render)
    // would spend it and fail the second time.
    if (redeemed.current) return;
    redeemed.current = true;

    // Drop the secret from the address bar before anything else can copy it.
    window.history.replaceState(null, "", "/support-login");

    // One teardown path for every failure after the cookie exists. Three
    // separate bugs came from having the cleanup inline in one branch: a
    // thrown verification skipped it entirely, and a successful sign-out still
    // left the Zustand store and localStorage holding an identity that
    // Login's own effect would redirect back into the app.
    const abandon = async (reason: string) => {
      let serverSessionEnded = false;
      try {
        const { error: signOutError } = await authClient.signOut();
        serverSessionEnded = !signOutError;
      } catch {
        serverSessionEnded = false;
      }

      // Local state has to go whether or not the server call worked: Login
      // redirects a stale cached identity straight back into protected UI.
      await clearAuth({ skipServerSignOut: true });

      setSessionMayBeLive(!serverSessionEnded);
      setError(
        serverSessionEnded
          ? `${reason} The session has been ended. Ask for a new link.`
          : `${reason} It could not be ended either. Close this browser` +
              " entirely, and ask an engineer to revoke this user's sessions.",
      );
    };

    /**
     * The one exit for every failure. Four shapes have now needed it -- a
     * missing grant, a refused redemption, an unverified success and a thrown
     * request -- so it is a policy rather than four branches: a failure says
     * nothing about whether a cookie exists, and offering "Go to sign in"
     * while one does drops the operator into that target's account.
     *
     * An unreadable probe counts as live. A needless sign-out costs nothing;
     * a missed one leaves a child's account open.
     */
    const failWith = async (reason: string) => {
      let existing: unknown = null;
      try {
        const { data: current } = await authClient.getSession({
          query: { disableCookieCache: true },
        });
        existing = current?.session ?? null;
      } catch {
        existing = { unknown: true };
      }

      if (existing) {
        await abandon(reason);
        return;
      }
      setError(reason);
    };

    const redeem = async () => {
      if (!grant) {
        await failWith("This link is missing its access code.");
        return;
      }

      try {
        const response = await fetch(
          `${AUTH_URL}/api/auth/support-login/redeem`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            credentials: "include",
            body: JSON.stringify({ grant }),
          },
        );

        if (!response.ok) {
          const body = (await response.json().catch(() => null)) as {
            message?: string;
          } | null;
          const reason =
            body?.message ||
            "This link has expired or has already been used. Ask for a new one.";

          await failWith(reason);
          return;
        }

        // Confirm which session we actually landed in. syncSession catches its
        // own failures and preserves a cached user, so it resolves even when
        // get-session fails — and the cookie is already the target's by now,
        // so trusting it would show the operator their own identity, with no
        // banner, while every API call ran as the student.
        await syncSession();
        const { data, error: sessionError } = await authClient.getSession({
          query: { disableCookieCache: true },
        });
        const impersonatedBy = (
          data?.session as { impersonatedBy?: string | null } | undefined
        )?.impersonatedBy;
        if (sessionError || !impersonatedBy) {
          await abandon(
            "Signed in, but the support session could not be confirmed.",
          );
          return;
        }

        // Land where this app's own sign-in lands this user: teachers on
        // /lessons, everyone else on /classrooms, read the same way Login
        // reads it. There is no `/content` route -- sending support sessions
        // there dropped the operator on a blank page, because the router has
        // no catch-all and an unmatched path renders nothing at all.
        const role =
          (
            data?.user as
              { account_role?: string | null; role?: string | null } | undefined
          )?.account_role ||
          (data?.user as { role?: string | null } | undefined)?.role ||
          "STUDENT";

        // A full load rather than a client-side navigate. The banner sits
        // above the router and stays mounted, and its useSession may already
        // have cached an unauthenticated result from before the redemption —
        // neither the raw fetch above nor the imperative getSession notifies
        // that store, so the banner could stay hidden for the whole session.
        // Reloading guarantees every consumer starts from the real cookie.
        window.location.assign(isTeacher(role) ? "/lessons" : "/classrooms");
      } catch {
        // Any exit that is not a confirmed success goes through the policy.
        await failWith(
          "Signed in, but the support session could not be verified.",
        );
      }
    };

    void redeem();
  }, [grant, clearAuth, syncSession]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50 px-4">
      <div className="w-full max-w-md rounded-lg border bg-white p-6 text-center shadow-sm">
        {error ? (
          <>
            <h1 className="mb-2 text-lg font-semibold text-gray-900">
              This support link cannot be used
            </h1>
            <p className="mb-4 text-sm text-gray-600">{error}</p>
            {/* No navigation while a redeemed session may still be live:
                Login calls getSession on mount and forwards any valid cookie
                straight into the app, so "Go to sign in" would walk back into
                the target's account. */}
            {sessionMayBeLive ? null : (
              <button
                type="button"
                onClick={() => navigate("/login", { replace: true })}
                className="rounded-md bg-gray-900 px-4 py-2 text-sm font-semibold text-white"
              >
                Go to sign in
              </button>
            )}
          </>
        ) : (
          <>
            <h1 className="mb-2 text-lg font-semibold text-gray-900">
              Opening support session…
            </h1>
            <p className="text-sm text-gray-600">
              Signing you in to the account this link was made for.
            </p>
          </>
        )}
      </div>
    </div>
  );
};

export default SupportLogin;
